import { useTranslation } from 'react-i18next';
import { MessageCircle, Phone, Share2 } from 'lucide-react';
import { cn } from '../../lib/cn.js';

/** Israeli local numbers become international for wa.me: 050… → 97250…. */
export function whatsappHref(phone: string | null): string | null {
  const digits = phone?.replace(/\D/g, '') ?? '';
  if (!digits) return null;
  return `https://wa.me/${digits.startsWith('0') ? `972${digits.slice(1)}` : digits}`;
}

const round = 'h-12 w-12 rounded-full flex items-center justify-center shadow-sm transition-opacity';

/**
 * The foot of the gallery page: WhatsApp at the start (right in Hebrew), the
 * share in the middle, the phone at the end. A button with nowhere to go is
 * shown dimmed rather than hidden, so the bar keeps its shape.
 */
export function ContactBar({ phone, onShare }: { phone: string | null; onShare: () => void }) {
  const { t } = useTranslation();
  const whatsapp = whatsappHref(phone);
  const tel = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : null;
  return (
    <nav aria-label={t('miniSite.public.contact')} className="flex items-center justify-between px-6 py-3">
      <a
        href={whatsapp ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('miniSite.whatsapp')}
        aria-disabled={!whatsapp}
        className={cn(round, 'bg-[#25D366] text-white', !whatsapp && 'opacity-40 pointer-events-none')}
      >
        <MessageCircle className="h-6 w-6" />
      </a>
      <button
        type="button"
        onClick={onShare}
        aria-label={t('miniSite.shareGallery')}
        className={cn(round, 'bg-primary text-primary-foreground')}
      >
        <Share2 className="h-5 w-5" />
      </button>
      <a
        href={tel ?? undefined}
        aria-label={t('miniSite.call')}
        aria-disabled={!tel}
        className={cn(round, 'bg-card border border-border text-foreground', !tel && 'opacity-40 pointer-events-none')}
      >
        <Phone className="h-5 w-5" />
      </a>
    </nav>
  );
}
